import { isPico8Palette, nearestPico8Index } from './pico8';

const HEX = '0123456789abcdef';

// Mappar palett-index till PICO-8-index (eller närmaste färg)
export function mapToPicoIndex(v: number, palette: string[]) {
  // NEW: secondary (128..143) eller -16..-1 => skriv basindex 0..15
  if ((v & 0x80) !== 0 || v < 0) return v & 15;

  if (isPico8Palette(palette) && v >= 0 && v < 16) return v | 0;
  const hex = palette?.[v] ?? '#000000';
  return nearestPico8Index(hex);
}

export function parseGfxLines(originalLines: string[]): number[][] {
  const H = 128, W = 128;
  const grid = Array.from({ length: H }, () => Array(W).fill(0));
  for (let y = 0; y < H; y++) {
    const line = (originalLines[y] || '').trim().toLowerCase();
    for (let x = 0; x < W; x++) {
      const ch = line[x] || '0';
      const v = parseInt(ch, 16);
      grid[y][x] = Number.isNaN(v) ? 0 : v;
    }
  }
  return grid;
}

function injectSecondaryPalScript(p8Text: string, usedSecondary: boolean[]) {
  const L_START = '-- PixelPaint: secondary palette remap BEGIN';
  const L_END = '-- PixelPaint: secondary palette remap END';

  const lines = p8Text.split('\n');
  const luaIdx = lines.findIndex(l => l.trim() === '__lua__');

  // Bygg remap-linjer
  const palLines: string[] = [];
  for (let i = 0; i < 16; i++) {
    if (usedSecondary[i]) palLines.push(`pal(${i}, ${128 + i}, 1)`);
  }

  // Om inga secondary används: ta bort ev. gammalt block och returnera
  if (palLines.length === 0) {
    const s = lines.findIndex(l => l.includes(L_START));
    const e = lines.findIndex(l => l.includes(L_END));
    if (s !== -1 && e !== -1 && e >= s) {
      lines.splice(s, e - s + 1);
      return lines.join('\n');
    }
    return p8Text;
  }

  const block = [L_START, 'pal()', ...palLines, L_END];

  if (luaIdx !== -1) {
    // Ersätt gammalt block om det finns, annars lägg direkt efter __lua__
    const s = lines.findIndex((l, i) => i > luaIdx && l.includes(L_START));
    const e = lines.findIndex((l, i) => i > luaIdx && l.includes(L_END));
    if (s !== -1 && e !== -1 && e >= s) {
      lines.splice(s, e - s + 1, ...block);
    } else {
      lines.splice(luaIdx + 1, 0, ...block);
    }
    return lines.join('\n');
  } else {
    // Ingen __lua__: lägg till en med blocket
    return p8Text.replace(/\s*$/, '\n') + `__lua__\n${block.join('\n')}\n`;
  }
}

export function serializeGfxGrid(grid: number[][]): string[] {
  const H = 128, W = 128;
  const out: string[] = [];
  for (let y = 0; y < H; y++) {
    let s = '';
    for (let x = 0; x < W; x++) s += HEX[grid[y][x] & 15];
    out.push(s);
  }
  return out;
}

// Bygg occupancy (true = tile upptagen) från 128×128 __gfx__
function buildTileOccupancy(grid: number[][]) {
  const T = 16;
  const occ = Array.from({ length: T }, () => Array(T).fill(false));
  for (let ty = 0; ty < T; ty++) {
    for (let tx = 0; tx < T; tx++) {
      let used = false;
      for (let y = 0; y < 8 && !used; y++) {
        for (let x = 0; x < 8 && !used; x++) {
          if (grid[ty * 8 + y][tx * 8 + x] !== 0) used = true;
        }
      }
      occ[ty][tx] = used;
    }
  }
  return occ;
}

// Hitta tomt tile-block från startindex (sekventiell packning)
export function findEmptyBlockOccFrom(
  occ: boolean[][],
  tilesW: number,
  tilesH: number,
  startIndex = 0
): { tx: number; ty: number } | null {
  const T = 16;
  const total = T * T;

  const tryAt = (tx: number, ty: number) => {
    if (tx + tilesW > T || ty + tilesH > T) return false;
    for (let by = 0; by < tilesH; by++) {
      for (let bx = 0; bx < tilesW; bx++) {
        if (occ[ty + by][tx + bx]) return false;
      }
    }
    return true;
  };

  for (let i = startIndex; i < total; i++) {
    const ty = Math.floor(i / T);
    const tx = i % T;
    if (tryAt(tx, ty)) return { tx, ty };
  }
  for (let i = 0; i < Math.min(startIndex, total); i++) {
    const ty = Math.floor(i / T);
    const tx = i % T;
    if (tryAt(tx, ty)) return { tx, ty };
  }
  return null;
}

// Merge flera sprites in i .p8-textens __gfx__-sektion (hanterar 8×8 och 16×16)
export function mergeManySpritesIntoP8Text(
  p8Text: string,
  sprites: { pixels: number[][]; palette: string[] }[]
) {
  const text = p8Text.replace(/\r\n/g, '\n');
  const lines = text.split('\n');
  let gfxStart = lines.findIndex(l => l.trim() === '__gfx__');
  if (gfxStart === -1) { lines.push('__gfx__'); gfxStart = lines.length - 1; }
  const nextSectionRel = lines.slice(gfxStart + 1).findIndex(l => /^__\w+__$/i.test(l.trim()));
  const gfxEnd = nextSectionRel === -1 ? lines.length : gfxStart + 1 + nextSectionRel;

  const existingGfxLines = lines.slice(gfxStart + 1, gfxEnd);
  const grid = parseGfxLines(existingGfxLines);
  const occ = buildTileOccupancy(grid);

  const ordered = [...sprites].sort((a, b) => {
    const sa = (a.pixels?.length === 16 || a.pixels?.[0]?.length === 16) ? 1 : 0;
    const sb = (b.pixels?.length === 16 || b.pixels?.[0]?.length === 16) ? 1 : 0;
    return sb - sa;
  });

  let mergedCount = 0;
  let scanStart = 0;
  const usedSecondary: boolean[] = Array(16).fill(false);

  for (const s of ordered) {
    const h = s.pixels?.length || 8;
    const w = s.pixels?.[0]?.length || h;
    const size: 8 | 16 = (w === 16 || h === 16) ? 16 : 8;
    const needW = size === 16 ? 2 : 1;
    const needH = size === 16 ? 2 : 1;

    const pos = findEmptyBlockOccFrom(occ, needW, needH, scanStart);
    if (!pos) break;

    for (let by = 0; by < needH; by++)
      for (let bx = 0; bx < needW; bx++)
        occ[pos.ty + by][pos.tx + bx] = true;

    for (let y = 0; y < size && y < h; y++) {
      for (let x = 0; x < size && x < w; x++) {
        const v = s.pixels?.[y]?.[x] ?? 0;
        if ((v & 0x80) !== 0 || v < 0) usedSecondary[v < 0 ? (v + 16) & 15 : (v & 15)] = true;
        grid[pos.ty * 8 + y][pos.tx * 8 + x] = mapToPicoIndex(v, s.palette);
      }
    }

    mergedCount++;
    scanStart = pos.ty * 16 + (pos.tx + needW);
    if (scanStart >= 256) scanStart = 0;
  }

  const updatedGfx = serializeGfxGrid(grid);
  const pre = lines.slice(0, gfxStart + 1);
  const post = lines.slice(gfxEnd);
  let returnText = [...pre, ...updatedGfx, ...post].join('\n');

  returnText  = injectSecondaryPalScript(returnText, usedSecondary);

  return { text: returnText, mergedCount };
}